import PaginatedTable from '../../components/ui/PaginatedTable';
import PermissionButton from "../../components/ui/PermissionButton";
import FinancialScopeNotice from '../../components/ui/FinancialScopeNotice';
import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import { Search, Download, Printer } from "lucide-react";
import BankTransactionViewModal from "./BankTransactionViewModal";

export default function BankBook() {
  const [search, setSearch] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [banks, setBanks] = useState([]);
  const [summary, setSummary] = useState({
    receipts: 0,
    payments: 0,
    closing: 0,
  });
  const [bank, setBank] = useState("All");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    loadBankBook();
  }, []);

  async function loadBankBook() {
    try {
      const res = await fetch("/api/bank-book", {
        credentials: "include",
      });

      const data = await res.json();

      if (data.status === "success") {
        setTransactions(data.transactions || []);
        setBanks(data.banks || []);
        setSummary(data.summary || {});
      }
    } catch (err) {
      console.error(err);
    }
  }

  const filtered = useMemo(() => {
    return transactions.filter((t) => {
      const matchesSearch =
        (t.voucher || "").toLowerCase().includes(search.toLowerCase()) ||
        (t.description || "").toLowerCase().includes(search.toLowerCase()) ||
        (t.reference || "").toLowerCase().includes(search.toLowerCase());

      const matchesBank = bank === "All" || t.bank === bank;

      return matchesSearch && matchesBank;
    });
  }, [transactions, search, bank]);

  return (
    <DashboardLayout>
      <FinancialScopeNotice/>
      <div className="space-y-10">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-serif">Bank Book</h1>

            <p className="text-sm text-gray-400">
              Track all bank receipts & payments
            </p>
          </div>

          <div className="flex gap-3">
            <PermissionButton permissionModule="accounts" className="p-3 rounded-xl bg-white/5 border border-white/10">
              <Download size={18} />
            </PermissionButton>

            <PermissionButton permissionModule="accounts" className="p-3 rounded-xl bg-white/5 border border-white/10">
              <Printer size={18} />
            </PermissionButton>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <Card
            title="Total Payments"
            value={`₹${Number(summary.payments).toLocaleString("en-IN")}`}
            color="text-red-400"
          />

          <Card
            title="Total Receipts"
            value={`₹${Number(summary.receipts).toLocaleString("en-IN")}`}
            color="text-emerald-400"
          />

          <Card
            title="Closing Balance"
            value={`₹${Number(summary.closing).toLocaleString("en-IN")}`}
            color="text-amber-400"
          />
        </div>

        <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
          <div className="flex gap-4 mb-6">
            <div className="relative flex-1">
              <Search
                size={18}
                className="absolute left-3 top-3 text-gray-500"
              />

              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-black/30 border border-white/10"
              />
            </div>

            <select
              value={bank}
              onChange={(e) => setBank(e.target.value)}
              className="px-4 rounded-xl bg-black/30 border border-white/10"
            >
              <option value="All">All</option>
              {banks.map((b) => (
                <option key={b.id} value={b.name}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <PaginatedTable className="w-full text-sm">
            <thead className="text-gray-400">
              <tr>
                <th className="text-left py-3">Voucher</th>
                <th>Date</th>
                <th>Bank</th>
                <th>Category</th>
                <th>Receipt</th>
                <th>Payment</th>
                <th>Balance</th>
              </tr>
            </thead>

            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-gray-400">
                    No bank transactions found.
                  </td>
                </tr>
              ) : (
                filtered.map((t) => (
                  <tr
                    key={t.id}
                    onClick={() => setSelected(t)}
                    className="border-t border-white/10 hover:bg-white/5 cursor-pointer"
                  >
                    <td className="py-4">{t.voucher}</td>
                    <td>{t.date}</td>
                    <td>{t.bank}</td>
                    <td>{t.reference || "-"}</td>
                    <td className="text-emerald-400">
                      {t.receipt
                        ? `₹${Number(t.receipt).toLocaleString("en-IN")}`
                        : "-"}
                    </td>
                    <td className="text-red-400">
                      {t.payment
                        ? `₹${Number(t.payment).toLocaleString("en-IN")}`
                        : "-"}
                    </td>
                    <td className="text-amber-400">
                      ₹{Number(t.balance).toLocaleString("en-IN")}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </PaginatedTable>
        </div>
      </div>

      {selected && (
        <BankTransactionViewModal
          transaction={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </DashboardLayout>
  );
}

function Card({ title, value, color }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
      <p className="text-xs uppercase tracking-widest text-gray-400">{title}</p>
      <h2 className={`mt-3 text-3xl font-serif ${color}`}>{value}</h2>
    </div>
  );
}
